import React from 'react';
import {AppErrorFallback, AppErrorFallbackProps} from './AppErrorFallback';

export interface AppErrorBoundaryFallbackProps extends AppErrorFallbackProps {
  info: React.ErrorInfo;
  clearError: () => void;
}

interface Props {
  children?: React.ReactNode;
  FallbackComponent?: React.ComponentType<AppErrorBoundaryFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {error: null, errorInfo: null};
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({errorInfo});
    this.props.onError?.(error, errorInfo);
  }

  clearError = () => {
    this.setState({error: null, errorInfo: null});
  };

  render() {
    const {error, errorInfo} = this.state;

    if (error) {
      const Fallback = this.props.FallbackComponent ?? AppErrorFallback;
      return <Fallback error={error} info={errorInfo ?? {componentStack: ''}} clearError={this.clearError} />;
    }

    return this.props.children;
  }
}
